import { cn } from '@/utils/utils.ts';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMemo, useState } from 'react';
import { FileSpreadsheet, LoaderIcon, MoreHorizontal } from 'lucide-react';
import { TrashIcon } from '@radix-ui/react-icons';
import { useNotificationsStore } from '@/services/store/use-notifications-store.ts';
import { useGetNotificationsQuery } from '@/features/notifications/hooks/queries/get-all-notifications.ts';
import { TNotification } from '@/features/notifications/schemas/notifications.schemas.ts';
import { useCSVExport } from '@/hooks/use-csv-export.ts';

export function NotificationHeaderMoreSettingsPopover() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 rounded-full p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <MoreHorizontal className="h-4 w-4 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="end" sideOffset={4}>
        <div className="space-y-1" role="menu">
          <ExportCSVButton />
          <button
            disabled={true}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
              'text-left',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
              'disabled:pointer-events-none disabled:opacity-50'
            )}
            role="menuitem"
          >
            <TrashIcon className="h-4 w-4" />
            Delete all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ExportCSVButton() {
  const notificationFilter = useNotificationsStore((state) => state.notificationFilter);
  const { data } = useGetNotificationsQuery({ notificationFilter });
  const notifications = data?.pages;

  const csvColumns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        accessor: (notification: TNotification) => notification.title,
      },
      {
        key: 'description',
        header: 'Description',
        accessor: (notification: TNotification) => notification.description,
      },
      {
        key: 'createdAt',
        header: 'Created At',
        accessor: (notification: TNotification) => notification.createdAt,
        formatter: (date: string | Date) => new Date(date).toLocaleString('ro-RO'),
      },
    ],
    []
  );

  const { downloadCSV, isExporting } = useCSVExport({
    data: notifications,
    columns: csvColumns,
    filename: `EMS_Notifications_${new Date().toLocaleDateString('ro-RO')}`,
    dateFormat: 'dd-MM-yyyy',
    locale: 'ro-RO',
  });

  return (
    <button
      onClick={() => downloadCSV()}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
        'text-left',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50'
      )}
      role="menuitem"
    >
      {isExporting ? (
        <LoaderIcon className="size-4 animate-spin text-gray-800" aria-hidden="true" />
      ) : (
        <FileSpreadsheet className="size-4" />
      )}
      {isExporting ? 'Exporting...' : 'Export as CSV'}
    </button>
  );
}
